/**
 * FloatingBugReportButton Component
 *
 * A floating action button (FAB) positioned at the bottom-left corner that
 * allows anyone (including guests) to quickly report bugs. Captures a screenshot
 * before opening the dialog to ensure the modal isn't visible in the screenshot.
 *
 * Features:
 * - Fixed position bottom-left
 * - Red color scheme for bug reporting
 * - Visible to all users (authenticated and guests)
 * - Integrates with console capture hook
 * - Captures screenshot BEFORE opening dialog (prevents modal in screenshot)
 * - Disabled state while capturing screenshot
 *
 * @author American Police Training
 * @version 1.2.0
 */
import React, { useState } from 'react';
import { Fab, Tooltip, Zoom, useTheme, useMediaQuery } from '@mui/material';
import BugReportIcon from '@mui/icons-material/BugReport';
import { useConsoleCapture } from '../../hooks/useConsoleCapture';
import BugReportDialog from './BugReportDialog';
import { getImageProxyUrl } from '../../utils/imageUtils';
import { logger } from "../../utils/logger";
// import { logger } from "../../utils/logger";
interface FloatingBugReportButtonProps {
    forceShow?: boolean; // Force show even on mobile (for use outside speed dial)
}
export const FloatingBugReportButton: React.FC<FloatingBugReportButtonProps> = ({ forceShow = false }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [dialogOpen, setDialogOpen] = useState(false);
    const [capturedScreenshot, setCapturedScreenshot] = useState<string | null>(null);
    const [capturing, setCapturing] = useState(false);
    const { getCapturedLogs } = useConsoleCapture();
    const handleOpen = async (): Promise<void> => {
        // Capture screenshot BEFORE opening dialog
        setCapturing(true);
        try {
            // Note: Images should already be using proxy URLs from imageUtils,
            // but we keep this as a fallback for any direct CDN references
            const cdnImages = document.querySelectorAll('img[src*="cdn.americanpolicetraining.com"]');
            const originalSrcs = new Map<HTMLImageElement, string>();
            cdnImages.forEach((img) => {
                const htmlImg = img as HTMLImageElement;
                originalSrcs.set(htmlImg, htmlImg.src);
                // Replace CDN URL with proxy URL
                const cdnPath = htmlImg.src.replace(/https?:\/\/cdn\.americanpolicetraining\.com\//, '');
                htmlImg.src = `/cdn/${cdnPath}`;
            });
            // Wait a moment for images to load with new URLs (only if we found CDN images)
            if (cdnImages.length > 0) {
                await new Promise(resolve => setTimeout(resolve, 200));
            }
            // Capture the page as it currently looks (without the modal)
            const html2canvas = (await import('html2canvas')).default;
            const canvas = await html2canvas(document.body, {
                allowTaint: false,
                useCORS: true,
                logging: false,
                width: window.innerWidth,
                height: window.innerHeight,
                windowWidth: window.innerWidth,
                windowHeight: window.innerHeight,
            });
            // Restore original CDN URLs
            originalSrcs.forEach((originalSrc, img) => {
                img.src = originalSrc;
            });
            const dataUrl = canvas.toDataURL('image/png', 0.8);
            setCapturedScreenshot(dataUrl);
        }
        catch (err) {
            logger.error('Failed to capture screenshot:', err, {});
            // Still open dialog even if screenshot fails
            setCapturedScreenshot(null);
        }
        finally {
            setCapturing(false);
            // Open dialog after screenshot is captured
            setDialogOpen(true);
        }
    };
    const handleClose = (): void => {
        setDialogOpen(false);
        // Clear screenshot when dialog closes
        setCapturedScreenshot(null);
    };
    // On mobile, hide the FAB unless forceShow is true (will be handled by speed dial)
    if (isMobile && !forceShow) {
        return (<BugReportDialog open={dialogOpen} onClose={handleClose} getCapturedLogs={getCapturedLogs} initialScreenshot={capturedScreenshot}/>);
    }
    return (<>
      <Zoom in={true} timeout={300}>
        <Tooltip title={capturing ? "Capturing screenshot..." : "Report a Bug"} placement="right" arrow>
          <span>
            <Fab color="error" aria-label="report bug" onClick={handleOpen} disabled={capturing} sx={{
            position: 'fixed',
            bottom: 16,
            left: 16,
            zIndex: 9999,
            '&:hover': {
                transform: 'scale(1.05)',
                transition: 'transform 0.2s ease-in-out',
            },
        }}>
              <BugReportIcon />
            </Fab>
          </span>
        </Tooltip>
      </Zoom>

      <BugReportDialog open={dialogOpen} onClose={handleClose} getCapturedLogs={getCapturedLogs} initialScreenshot={capturedScreenshot}/>
    </>);
};
// Export the bug report handler for use in speed dial
export const useBugReportHandler = () => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [capturedScreenshot, setCapturedScreenshot] = useState<string | null>(null);
    const [capturing, setCapturing] = useState(false);
    const { getCapturedLogs } = useConsoleCapture();
    const handleOpen = async (): Promise<void> => {
        setCapturing(true);
        try {
            // Replace all external image URLs with proxy URLs to avoid CORS issues
            // This includes both cdn.americanpolicetraining.com and DigitalOcean Spaces URLs
            const allImages = document.querySelectorAll('img[src]');
            const originalSrcs = new Map<HTMLImageElement, string>();
            const imagesToReplace: HTMLImageElement[] = [];
            
            allImages.forEach((img) => {
                const htmlImg = img as HTMLImageElement;
                const src = htmlImg.src;
                
                // Check if this is an external URL that needs proxying
                const isExternalCdn = src.includes('cdn.americanpolicetraining.com') ||
                                     src.includes('digitaloceanspaces.com') ||
                                     src.includes('apt.atl1.digitaloceanspaces.com');
                
                const isLocal = src.startsWith(window.location.origin) ||
                               src.includes('image-proxy') ||
                               src.startsWith('/cdn/');

                if (isExternalCdn && !isLocal) {
                    originalSrcs.set(htmlImg, src);
                    imagesToReplace.push(htmlImg);

                    let proxyPath = '';
                    if (src.includes('digitaloceanspaces.com')) {
                        const match = src.match(/digitaloceanspaces\.com\/(.+)$/);
                        if (match) proxyPath = match[1];
                    } else if (src.includes('cdn.americanpolicetraining.com')) {
                        proxyPath = src.replace(/https?:\/\/cdn\.americanpolicetraining\.com\//, '');
                    }

                    if (proxyPath) {
                        htmlImg.src = getImageProxyUrl(proxyPath);
                    }
                }
            });
            
            // Wait for images to load with new URLs if we replaced any
            if (imagesToReplace.length > 0) {
                // Wait for all images to load
                await Promise.all(
                    imagesToReplace.map((img) => {
                        return new Promise<void>((resolve) => {
                            if (img.complete) {
                                resolve();
                            } else {
                                img.onload = () => resolve();
                                img.onerror = () => resolve(); // Continue even if image fails
                                // Timeout after 2 seconds
                                setTimeout(() => resolve(), 2000);
                            }
                        });
                    })
                );
                // Additional small delay to ensure rendering
                await new Promise(resolve => setTimeout(resolve, 200));
            }
            
            const html2canvas = (await import('html2canvas')).default;
            const canvas = await html2canvas(document.body, {
                allowTaint: false,
                useCORS: true,
                logging: false,
                width: window.innerWidth,
                height: window.innerHeight,
                windowWidth: window.innerWidth,
                windowHeight: window.innerHeight,
                // Ignore failed images to prevent CORS errors from blocking capture
                onclone: (clonedDoc) => {
                    // Remove any images that failed to load in the cloned document
                    const failedImages = clonedDoc.querySelectorAll('img[src*="digitaloceanspaces.com"], img[src*="cdn.americanpolicetraining.com"]');
                    failedImages.forEach((img) => {
                        const htmlImg = img as HTMLImageElement;
                        if (!htmlImg.complete || htmlImg.naturalHeight === 0) {
                            htmlImg.style.display = 'none';
                        }
                    });
                },
            });
            
            // Restore original URLs
            originalSrcs.forEach((originalSrc, img) => {
                img.src = originalSrc;
            });
            
            const dataUrl = canvas.toDataURL('image/png', 0.8);
            setCapturedScreenshot(dataUrl);
        }
        catch (err) {
            logger.error('Failed to capture screenshot:', err, {});
            setCapturedScreenshot(null);
        }
        finally {
            setCapturing(false);
            setDialogOpen(true);
        }
    };
    const handleClose = (): void => {
        setDialogOpen(false);
        setCapturedScreenshot(null);
    };
    return {
        dialogOpen,
        capturedScreenshot,
        capturing,
        handleOpen,
        handleClose,
        getCapturedLogs,
    };
};
export default FloatingBugReportButton;
